import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../services/api';
import '../../styles/courses.css';
import LessonForm from '../../components/forms/LessonForm';

// Função para determinar o tipo de conteúdo
const getContentType = (lesson) => {
  const hasVideo = lesson.content && (lesson.content.includes('youtube.com') || lesson.content.includes('youtu.be'));
  const hasPdf = lesson.materials && lesson.materials.some(m => m.type === 'PDF');
  if (hasVideo && hasPdf) return 'Vídeo + PDF';
  if (hasVideo) return 'Vídeo';
  if (hasPdf) return 'PDF';
  return 'Texto';
};

// Componente SortableLesson para ser envolvido com drag-and-drop
function SortableLesson({ lesson, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lesson.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 41, 59, 0.5)',
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{
        backgroundColor: isDragging ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 41, 59, 0.5)',
        '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.1)' },
        borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
      }}
      {...attributes}
    >
      <TableCell
        sx={{ color: '#06b6d4', textAlign: 'center', py: 1, width: '48px', cursor: isDragging ? 'grabbing' : 'grab' }}
        {...listeners}
      >
        <DragIndicatorIcon sx={{ fontSize: '1.25rem' }} />
      </TableCell>
      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
        {lesson.position}
      </TableCell>
      <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
        {lesson.title}
      </TableCell>
      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', maxWidth: 200 }}>
        {lesson.description || '-'}
      </TableCell>
      <TableCell sx={{ color: '#06b6d4' }}>
        {getContentType(lesson)}
      </TableCell>
      <TableCell
        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
      >
        {lesson.durationSeconds ? `${Math.round(lesson.durationSeconds / 60)} min` : '-'}
      </TableCell>
      <TableCell>
        <IconButton
          size="small"
          onClick={() => onEdit(lesson)}
          sx={{ color: '#3b82f6' }}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(lesson.id)}
          sx={{ color: '#ef4444' }}
        >
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export default function ModuleDetail() {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lessonDialog, setLessonDialog] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      distance: 8,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data);
      
      const moduleData = courseRes.data.modules?.find(m => m.id === moduleId);
      setModule(moduleData);
      setLessons(moduleData?.lessons || []);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [courseId, moduleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateLesson = () => {
    setSelectedLesson(null);
    setLessonDialog(true);
  };

  const handleEditLesson = async (lesson) => {
    try {
      // Buscar aula completa com materiais
      const res = await api.get(`/lessons/${lesson.id}`);
      setSelectedLesson(res.data);
      setLessonDialog(true);
    } catch (err) {
      setError('Erro ao carregar aula para edição');
      console.error(err);
    }
  };

  const handleSaveLesson = async (formData) => {
    try {
      // API call is handled by LessonForm, just update UI
      setSuccess(selectedLesson ? 'Aula atualizada com sucesso' : 'Aula criada com sucesso');
      setLessonDialog(false);
      await loadData();
    } catch (err) {
      setError('Erro ao salvar aula');
      console.error(err);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm('Tem certeza que deseja excluir esta aula?')) {
      try {
        setLoading(true);
        await api.delete(`/lessons/${lessonId}`);
        setSuccess('Aula excluída com sucesso');
        await loadData();
      } catch (err) {
        setError('Erro ao excluir aula');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = lessons.findIndex(l => l.id === active.id);
      const newIndex = lessons.findIndex(l => l.id === over.id);

      const newLessons = arrayMove(lessons, oldIndex, newIndex);
      setLessons(newLessons);

      // Recalcular posições
      const lessonUpdates = newLessons.map((lesson, index) => ({
        id: lesson.id,
        position: index + 1,
      }));

      try {
        // Enviar para o backend
        await api.post(`/lessons/reorder`, {
          moduleId,
          lessons: lessonUpdates,
        });
        setSuccess('Aulas reordenadas com sucesso');
      } catch (err) {
        setError('Erro ao reordenar aulas');
        console.error(err);
        // Reverter para a ordem anterior
        await loadData();
      }
    }
  };

  if (loading && !module) {
    return (
      <div className="modules-page">
        <div className="loading-container">
          <CircularProgress />
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 2 }}>
            Carregando...
          </Typography>
        </div>
      </div>
    );
  }

  if (error && !module) {
    return (
      <div className="modules-page">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <Box className="lessons-page">
      {/* Header */}
      <header className="management-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate(`/courses/${courseId}/modules`)}
            title="Voltar para módulos"
          >
            ← Voltar
          </button>
          <div className="header-title-section">
            <h1>{module?.title}</h1>
            <p className="header-subtitle">Gerenciar Aulas e Alunos</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="add-button-fab"
            onClick={handleCreateLesson}
            title="Adicionar nova aula"
          >
            +
          </button>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="breadcrumb-nav">
        <span
          className="breadcrumb-item"
          onClick={() => navigate('/courses')}
        >
          Cursos
        </span>
        <span className="breadcrumb-separator">›</span>
        <span
          className="breadcrumb-item"
          onClick={() => navigate(`/courses/${courseId}/modules`)}
        >
          {course?.title}
        </span>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-item active">{module?.title}</span>
      </nav>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
          {success}
        </Alert>
      )}

      {/* Content with Tabs */}
      <Card
        sx={{
          backgroundColor: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid rgba(59, 130, 246, 0.1)',
          borderRadius: '12px',
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        <CardContent sx={{ backgroundColor: 'rgba(30, 41, 59, 0.4)' }}>
          {lessons.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography sx={{ fontSize: '3rem', mb: 1 }}>📚</Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, mb: 1 }}>
                Nenhuma aula criada
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 2 }}>
                Clique no botão + para adicionar a primeira aula
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateLesson}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  borderRadius: '8px',
                  fontWeight: 600,
                }}
              >
                Adicionar Aula
              </Button>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                <Table sx={{ '& th': { backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#3b82f6', fontWeight: 600, width: '48px', textAlign: 'center' }}>⋮⋮</TableCell>
                      <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>Posição</TableCell>
                      <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>Título</TableCell>
                      <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>Descrição</TableCell>
                      <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>Tipo</TableCell>
                      <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>Duração</TableCell>
                      <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <SortableContext
                      items={lessons.map(l => l.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {lessons.map((lesson) => (
                        <SortableLesson
                          key={lesson.id}
                          lesson={lesson}
                          onEdit={handleEditLesson}
                          onDelete={handleDeleteLesson}
                        />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </TableContainer>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Lesson Form Modal */}
      <LessonForm
        open={lessonDialog}
        lesson={selectedLesson}
        module={module}
        onSave={handleSaveLesson}
        onClose={() => setLessonDialog(false)}
        loading={loading}
      />
    </Box>
  );
}
